import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { Link } from "@tanstack/react-router";
import EmptyState from "components/EmptyState";
import { InspectionStatusTag } from "components/InspectionStatusTag";
import { Notes } from "components/Notes";
import SecondaryButton from "components/buttons/SecondaryButton";
import { SeparatorIcon } from "components/icons/SeparatorIcon";
import { TransportSedelStatusTag } from "components/TransportSedelStatusTag";
import { WaterMeasurementStatusTag } from "components/WaterMeasurementStatusTag";
import { getActivityTypeName } from "enums/ActivityType.enum";
import { cn } from "helpers/classNames";
import { useState } from "react";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import { Uppdrag, UppdragStatus } from "types/uppdrag.type";
import { CreateUppgiftModal } from "../uppgifter/CreateUppgiftModal";
import { useCreateUppgiftLogic } from "../uppgifter/useCreateUppgiftLogic";
import { TransportSedelModal } from "../transportSedel/TransportSedelModal";
import { useCreateTransportSedelLogic } from "../transportSedel/useCreateTransportSedelLogic";
import { useCreateWasteLogic } from "../transportSedel/useCreateWasteLogic";
import { WasteModal } from "../transportSedel/WasteModal";
import {
  ActivityItem,
  buildUppdragActivities,
  isActivityDone,
} from "./uppdragActivities";

type Props = {
  uppdrag: Uppdrag;
};

export const OperatorUppgiftList = ({ uppdrag }: Props) => {
  // Samma modal-kedja som UppgiftTab: ny aktivitet -> ev. transportsedel -> avfall
  const [tomningSeparatorUuid, setTomningSeparatorUuid] = useState<
    string | undefined
  >();
  const transportSedelId =
    uppdrag.transportSedelList.find((x) => true)?.id ?? -1;

  const createWasteLogic = useCreateWasteLogic({
    transportSedel: { id: transportSedelId },
    uppdrag,
    separatorUuid: tomningSeparatorUuid,
    setTomningSeparatorUuid,
    isSeparatorDropdownHidden: false,
    isSetSeparatorDisabled: true,
  });

  const addTransportSedelLogic = useCreateTransportSedelLogic(
    uppdrag,
    createWasteLogic.openModal,
  );

  const createUppgiftLogic = useCreateUppgiftLogic({
    uppdrag,
    openWasteModal: createWasteLogic.openModal,
    openTransportSedelModal: addTransportSedelLogic.openModal,
    setTomningSeparatorUuid,
  });

  const { items, itemsWithoutSeparator, separatorGroups } =
    buildUppdragActivities(uppdrag);

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-x-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Aktiviteter
          {uppdrag.areAllUppgiftsCompleted && !!uppdrag.uppgiftCount && (
            <CheckCircleIcon className="h-4 w-4 text-green-600" />
          )}
        </h4>
        {items.length > 0 && uppdrag.status !== UppdragStatus.Completed && (
          <SecondaryButton size="small" onClick={createUppgiftLogic.openModal}>
            + Ny aktivitet
          </SecondaryButton>
        )}
      </div>

      <CreateUppgiftModal logic={createUppgiftLogic} />
      <TransportSedelModal
        title="Skapa transportsedel"
        logic={addTransportSedelLogic}
        actionText="Nästa"
      />
      <WasteModal
        title="Ny avfallstransport"
        logic={createWasteLogic}
        hideSeparatorDropdownIfNoSeparatorPreSelected
      />

      {items.length === 0 ? (
        <EmptyState
          onClick={createUppgiftLogic.openModal}
          header="Inga aktiviteter"
          subHeader="Lägg till din första aktivitet för att komma igång"
          buttonText="Ny aktivitet"
          iconType="uppgift"
        />
      ) : (
        <div className="flex flex-col gap-y-3">
          {itemsWithoutSeparator.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-75 bg-white">
              <ul className="divide-y divide-gray-75">
                {itemsWithoutSeparator.map((item) => (
                  <ActivityRow key={item.key} item={item} />
                ))}
              </ul>
            </div>
          )}
          {separatorGroups.map(({ separator, items: groupItems }) => (
            <div
              key={separator.id}
              className="overflow-hidden rounded-xl border border-gray-75 bg-white"
            >
              <div className="border-b border-gray-75 bg-gray-12 px-4 py-2.5">
                <Link
                  to="/app/kunder/$customerId/facility/$facilityId/separator/$separatorId"
                  params={{
                    customerId: separator.facility.customer.id.toString(),
                    facilityId: separator.facility.id.toString(),
                    separatorId: separator.id.toString(),
                  }}
                  className="flex items-center gap-x-1.5 text-sm font-semibold text-gray-800 active:text-gray-950"
                >
                  <SeparatorIcon className="shrink-0" />
                  <span className="truncate">
                    {getSeparatorDetailedLabel(separator)}
                  </span>
                </Link>
                {!!separator.notes && (
                  <div className="mt-1.5">
                    <Notes text={separator.notes} />
                  </div>
                )}
              </div>
              <ul className="divide-y divide-gray-75">
                {groupItems.map((item) => (
                  <ActivityRow key={item.key} item={item} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const ActivityRow = ({ item }: { item: ActivityItem }) => {
  const isDone = isActivityDone(item);
  return (
    <li>
      <Link
        {...item.link}
        className="flex min-h-[3rem] w-full items-center gap-x-3 px-4 py-2.5 transition-colors active:bg-gray-12"
      >
        {isDone ? (
          <CheckCircleIcon
            className="h-6 w-6 shrink-0 text-green-600"
            aria-hidden="true"
          />
        ) : (
          <span
            className="h-6 w-6 shrink-0 rounded-full border-2 border-dashed border-gray-200 bg-white"
            aria-hidden="true"
          />
        )}
        <span
          className={cn(
            "flex-1 text-sm",
            isDone ? "text-gray-400" : "font-semibold text-gray-900",
          )}
        >
          {getActivityTypeName(item.type)}
        </span>
        {!!item.inspectionStatus && (
          <InspectionStatusTag status={item.inspectionStatus} />
        )}
        {!!item.transportSedelStatus && (
          <TransportSedelStatusTag status={item.transportSedelStatus} />
        )}
        {!!item.waterMeasurementStatus && (
          <WaterMeasurementStatusTag status={item.waterMeasurementStatus} />
        )}
        <ChevronRightIcon
          className="h-4 w-4 shrink-0 text-gray-400"
          aria-hidden="true"
          strokeWidth={2.5}
        />
      </Link>
    </li>
  );
};
